import styles from './styles.module.scss';
import { useState, useEffect } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { Board } from '../../../../../components/Board';
import { useNavigate } from 'react-router-dom';
import { verifyRoutePermissions } from '../../../../../globalFunctions';
import { NavButton } from '../../../../../components/NavButton';
import { MusicCard } from './MusicCard';
import { User } from '../../../../../types';

const API_BASE = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_title || '';

type SoundItem = { id: number; som: string };

export const EfeitosSonoros: React.FC = () => {
    // áudio geral
    const [musicaPrincipal, setMusicaPrincipal] = useState<File | null>(null);

    // parabéns
    const [parabens, setParabens] = useState<SoundItem[]>([]);
    const [newParabensFile, setNewParabensFile] = useState<File | null>(null);
    const [uploadingParabens, setUploadingParabens] = useState(false);

    // erros
    const [erros, setErros] = useState<SoundItem[]>([]);
    const [combate, setCombate] = useState<any>([]);
    const [newErroFile, setNewErroFile] = useState<File | null>(null);
    const [uploadingErro, setUploadingErro] = useState(false);

    // auth / config
    const [userAuth, setUserAuth] = useState<User>({} as User);
    const requestConfig: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userAuth.id}`,
            Accept: 'application/json'
        }
    };

    const navigate = useNavigate();

    useEffect(() => {
        const user = verifyRoutePermissions(
            ['auth', 'email.verified', 'not.pending.update', 'logicmanager.classes'],
            navigate
        );
        if (typeof user === 'object') {
            setUserAuth(user);
        }
        fetchParabens();
        fetchErros();
        fetchBattle();
    }, [navigate]);

    // converte arquivo para dataURL (base64)
    const toBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = err => reject(err);
        });

    // ---------- PARABENS ----------
    const fetchParabens = async () => {
        try {
            const { data } = await axios.get(`${API_BASE}/api/parabens`, requestConfig);
            // espera array de objetos {id, som} ou, por segurança, array de strings
            if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
                setParabens((data as string[]).map((s, i) => ({ id: i + 1, som: s })));
            } else {
                setParabens(Array.isArray(data) ? (data as SoundItem[]) : []);
            }
        } catch (error) {
            console.error('Erro ao buscar parabéns:', error);
        }
    };

    const handleNewParabensFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) setNewParabensFile(e.target.files[0]);
    };

    const addParabens = async () => {
        if (!newParabensFile) return;
        setUploadingParabens(true);
        try {
            const base64 = await toBase64(newParabensFile);
            const { data } = await axios.post(
                `${API_BASE}/api/parabens`,
                { som: base64 },
                requestConfig
            );

            if (data && typeof data === 'object' && 'id' in data && 'som' in data) {
                setParabens(prev => [data as SoundItem, ...prev]);
            } else {
                await fetchParabens();
            }

            setNewParabensFile(null);
        } catch (error) {
            console.error('Erro ao adicionar parabéns:', error);
        } finally {
            setUploadingParabens(false);
        }
    };

    const deleteParabens = async (id: number) => {
        try {
            await axios.delete(`${API_BASE}/api/parabens/${id}`, requestConfig);
            setParabens(prev => prev.filter(p => p.id !== id));
        } catch (error) {
            console.error('Erro ao deletar parabéns:', error);
            await fetchParabens();
        }
    };

    // ---------- ERROS ----------
    const fetchErros = async () => {
        try {
            const { data } = await axios.get(`${API_BASE}/api/erros`, requestConfig);
            if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
                setErros((data as string[]).map((s, i) => ({ id: i + 1, som: s })));
            } else {
                setErros(Array.isArray(data) ? (data as SoundItem[]) : []);
            }
        } catch (error) {
            console.error('Erro ao buscar erros:', error);
        }
    };
    const fetchBattle = async () => {
        try {
            const { data } = await axios.get(`${API_BASE}/api/combate`, requestConfig);
            if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
                setCombate((data as string[]).map((s, i) => ({ id: i + 1, som: s })));
            } else {
                setCombate(Array.isArray(data) ? (data as SoundItem[]) : []);
            }
        } catch (error) {
            console.error('Erro ao buscar erros:', error);
        }
    };

    const handleNewErroFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) setNewErroFile(e.target.files[0]);
    };

    const addErro = async () => {
        if (!newErroFile) return;
        setUploadingErro(true);
        try {
            const base64 = await toBase64(newErroFile);
            const { data } = await axios.post(
                `${API_BASE}/api/erros`,
                { som: base64 },
                requestConfig
            );

            if (data && typeof data === 'object' && 'id' in data && 'som' in data) {
                setErros(prev => [data as SoundItem, ...prev]);
            } else {
                await fetchErros();
            }

            setNewErroFile(null);
        } catch (error) {
            console.error('Erro ao adicionar som de erro:', error);
        } finally {
            setUploadingErro(false);
        }
    };

    const deleteErro = async (id: number) => {
        try {
            await axios.delete(`${API_BASE}/api/erros/${id}`, requestConfig);
            setErros(prev => prev.filter(p => p.id !== id));
        } catch (error) {
            console.error('Erro ao deletar som de erro:', error);
            await fetchErros();
        }
    };

const [newCombateFile, setNewCombateFile] = useState<File | null>(null);
const [uploadingCombate, setUploadingCombate] = useState(false);

const handleNewCombateFile = (e: React.ChangeEvent<HTMLInputElement>) => {
  if (e.target.files && e.target.files[0]) setNewCombateFile(e.target.files[0]);
};

const addCombate = async () => {
  if (!newCombateFile) return;
  setUploadingCombate(true);
  try {
    const base64 = await toBase64(newCombateFile);
    const { data } = await axios.post(
      `${API_BASE}/api/combate`,
      { musica: base64 },
      requestConfig
    );

    if (data && typeof data === 'object' && 'id' in data && 'som' in data) {
      setCombate(prev => [data as SoundItem, ...prev]);
    } else {
      await fetchBattle();
    }

    setNewCombateFile(null);
  } catch (error) {
    console.error('Erro ao adicionar música de combate:', error);
  } finally {
    setUploadingCombate(false);
  }
};

const deleteCombate = async (id: number) => {
  try {
    await axios.delete(`${API_BASE}/api/combate/${id}`, requestConfig);
    setCombate(prev => prev.filter(c => c.id !== id));
  } catch (error) {
    console.error('Erro ao deletar música de combate:', error);
    await fetchBattle();
  }
};


    // handlers gerais (se quiser usar)
    const handleMusicaPrincipal = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setMusicaPrincipal(e.target.files[0]);
            const base64 = await toBase64(e.target.files[0]);
            const { data } = await axios.put(
                `${API_BASE}/api/updateMusicaCombate`,
                { musica: base64 },
                requestConfig
            );
        }
    };

    const goBackwards = () => navigate('/manager');

    return (
        <div className={styles.wrapper}>
            <Board wrapperClass={styles.board}>
                <div className={styles.container}>
                  {/* COMBATE */}
<section>
  <h2>Música Principal (Combate)</h2>
  <div
    style={{
      display: 'flex',
      gap: '.6rem',
      alignItems: 'center',
      marginBottom: '.8rem'
    }}
  >
    <input type='file' accept='audio/*' onChange={handleNewCombateFile} />
    <button onClick={addCombate} disabled={!newCombateFile || uploadingCombate}>
      {uploadingCombate ? 'Enviando...' : 'Adicionar Música'}
    </button>
    <button onClick={fetchBattle} style={{ marginLeft: 'auto' }}>
      Atualizar Lista
    </button>
  </div>

  <div>
    {combate.length === 0 && <p>Nenhuma música de combate cadastrada.</p>}
    <ul
      style={{
        listStyle: 'none',
        padding: 0,
        display: 'grid',
        gap: '.8rem'
      }}
    >
      {combate.map(c => (
        <li
          key={c.id}
          style={{
            display: 'flex',
            gap: '.6rem',
            alignItems: 'center'
          }}
        >
          <MusicCard id={c.id} url={c.musica} onDelete={deleteCombate} />
        </li>
      ))}
    </ul>
  </div>
</section>
                    <section>
                        <h2>Mensagens de Parabéns</h2>
                        <div
                            style={{
                                display: 'flex',
                                gap: '.6rem',
                                alignItems: 'center',
                                marginBottom: '.8rem'
                            }}
                        >
                            <input type='file' accept='audio/*' onChange={handleNewParabensFile} />
                            <button
                                onClick={addParabens}
                                disabled={!newParabensFile || uploadingParabens}
                            >
                                {uploadingParabens ? 'Enviando...' : 'Adicionar Parabéns'}
                            </button>
                            <button onClick={fetchParabens} style={{ marginLeft: 'auto' }}>
                                Atualizar Lista
                            </button>
                        </div>

                        <div>
                            {parabens.length === 0 && <p>Nenhum som de parabéns cadastrado.</p>}
                            <ul
                                style={{
                                    listStyle: 'none',
                                    padding: 0,
                                    display: 'grid',
                                    gap: '.8rem'
                                }}
                            >
                                {parabens.map(p => (
                                    <li
                                        key={p.id}
                                        style={{
                                            display: 'flex',
                                            gap: '.6rem',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <MusicCard id={p.id} url={p.som} onDelete={deleteParabens} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </section>

                    <hr style={{ margin: '1rem 0' }} />

                    {/* ERROS */}
                    <section>
                        <h2>Mensagens de Erro</h2>
                        <div
                            style={{
                                display: 'flex',
                                gap: '.6rem',
                                alignItems: 'center',
                                marginBottom: '.8rem'
                            }}
                        >
                            <input type='file' accept='audio/*' onChange={handleNewErroFile} />
                            <button onClick={addErro} disabled={!newErroFile || uploadingErro}>
                                {uploadingErro ? 'Enviando...' : 'Adicionar Erro'}
                            </button>
                            <button onClick={fetchErros} style={{ marginLeft: 'auto' }}>
                                Atualizar Lista
                            </button>
                        </div>

                        <div>
                            {erros.length === 0 && <p>Nenhum som de erro cadastrado.</p>}
                            <ul
                                style={{
                                    listStyle: 'none',
                                    padding: 0,
                                    display: 'grid',
                                    gap: '.8rem'
                                }}
                            >
                                {erros.map(p => (
                                    <li
                                        key={p.id}
                                        style={{
                                            display: 'flex',
                                            gap: '.6rem',
                                            alignItems: 'center'
                                        }}
                                    >
                                      <MusicCard id={p.id}  url={p.som} onDelete={deleteErro} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </section>

                    <hr style={{ margin: '1rem 0' }} />
                </div>
            </Board>

            <NavButton
                direction='left'
                style={{ left: '0.4vw', top: '2vw', zIndex: '20' }}
                onClick={goBackwards}
            />
        </div>
    );
};
